import { Col, Form, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';
import SaveButtonComponent from 'components/molecules/SaveButtonComponent';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';

import identifikaceEmitenta, { IIdentifikaceEmitenta } from 'services/akcionar/emise/identifikaceEmitenta';
import novyIsinSend, { ESendEmisniUdaje, IEmisniUdaje, emisniUdaje } from 'services/akcionar/emise/novyIsinSend';
import zadostZmenaApi, { IisinZmena } from 'services/akcionar/emise/zmenaISIN/zadostZmena';

import { ERozcestnikOptions } from 'enums/isin/rozcestnikOptions';
import { EDruhCp } from 'enums/isin/udaje/druhCp';
import routePathEnum from 'enums/routePathEnum';

interface IZadostPosila {
    identifikacePutData?: IIdentifikaceEmitenta;
    zadostID?: number;
    druhCP?: EDruhCp;
    udajeEmise?: IEmisniUdaje;
    prevStep: () => void;
    mode?: ERozcestnikOptions;
    zruseniData?: IisinZmena | undefined;
}

const ZadostPosila: React.FC<IZadostPosila> = ({
    zadostID,
    prevStep,
    udajeEmise,
    identifikacePutData,
    druhCP,
    mode = ERozcestnikOptions.NOVY,
    zruseniData
}) => {
    const { aktivniFirma, profily } = useContext(Context);
    const { navigateWorkaround } = useNavigateWorkaround();
    const [form] = Form.useForm();
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [saveModalMessage, setSaveModalMessage] = useState<string>('');

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (value: any) => {
        setLoading(true);
        if (mode === ERozcestnikOptions.ZRUSIT) {
            zadostZmenaApi('POST', zadostID!, 'Zruseni', 'Zruseni', {
                ...zruseniData,
                ...value
            })
                .then(() => novyIsinSend(zadostID ? zadostID : 0).then(() => setOpenModal(true)))
                .finally(() => setLoading(false));
        } else if (mode === ERozcestnikOptions.REGISTRATION) {
            identifikaceEmitenta('POST', zadostID!, identifikacePutData, 'Registration')
                .then(() => {
                    novyIsinSend(zadostID ? zadostID : 0)
                        .then(() => setOpenModal(true))
                        .finally(() => setLoading(false));
                })
                .catch(() => setLoading(false));
        } else {
            const data = udajeEmise && {
                ...(udajeEmise as {}),
                ...value
            };
            druhCP && saving
                ? emisniUdaje('put', ESendEmisniUdaje[druhCP], zadostID ? zadostID : 0, data)
                      .then(() => {
                          setSaveModalMessage('ULOZENI_DAT_200');
                          setSaving(false);
                          setLoading(false);
                      })
                      .catch(() => {
                          setSaveModalMessage('CHYBA_ULOZENI_DAT');
                          setLoading(false);
                          setSaving(false);
                      })
                : druhCP &&
                  emisniUdaje('post', ESendEmisniUdaje[druhCP], zadostID ? zadostID : 0, data).then(() =>
                      novyIsinSend(zadostID ? zadostID : 0).then(() => setOpenModal(true))
                  );
        }
    };

    useEffect(() => {
        saving && onFinish(form.getFieldsValue());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saving]);

    return (
        <LoadingWrapper loading={loading}>
            <FormWrapper outForm={form} onFinish={onFinish}>
                {mode === ERozcestnikOptions.ZMENA && (
                    <div
                        style={{
                            marginTop: 45,
                            display: 'block',
                            position: 'absolute',
                            right: 0
                        }}
                    >
                        <SaveButtonComponent
                            onClick={() => {
                                setSaving(true);
                            }}
                            modalText={saveModalMessage}
                            id="saveBtn"
                            onModalClose={() => setSaveModalMessage('')}
                        />
                    </div>
                )}
                <Paragraph text="ISIN_PODPIS_PODNADPIS" />
                <Title level={4} text={'ISIN_PODPIS_PODAL'} />
                <InputComponent
                    label="JMENO"
                    name="jmeno"
                    type="text"
                    maxLength={100}
                    disabled={!!profily?.jmeno}
                    initialValue={profily?.jmeno}
                />
                <InputComponent
                    label="PRIJMENI"
                    name="prijmeni"
                    type="text"
                    maxLength={100}
                    disabled={!!profily?.prijmeni}
                    initialValue={profily?.prijmeni}
                />
                <Row className="button-row">
                    <Col>
                        <ButtonComponent buttontext="ZPET" onClick={prevStep} type="default" id="isinZpetBtn" />
                    </Col>
                    <Col>
                        <ButtonComponent buttontext="ODESLAT" onClick={() => form.submit()} id="isinOdeslatBtn" />
                    </Col>
                </Row>
            </FormWrapper>
            <Modal
                title={<TranslateText>{`FORMULARE_ZADOST_${mode}`}</TranslateText>}
                open={openModal}
                onCancel={() =>
                    aktivniFirma
                        ? navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.EMISE_PREHLED}`)
                        : ''
                }
                footer={null}
            >
                <Row
                    style={{
                        paddingBottom: 16
                    }}
                >
                    <b>
                        <Paragraph text={'ZADOST_ODESLANA'} />
                    </b>
                </Row>
                <Row
                    style={{
                        paddingBottom: 36
                    }}
                >
                    <Paragraph text={'AMLZ_USPECH_TEXT'} />
                </Row>
                <AnchorComponent
                    link={true}
                    to={aktivniFirma ? `/firma/${aktivniFirma?.iducet}${routePathEnum.EMISE_PREHLED}` : ''}
                >
                    <ButtonComponent
                        style={{
                            marginRight: 20
                        }}
                        buttontext="POKRACOVAT"
                        id="isinPokracovatBtn"
                    />
                </AnchorComponent>
            </Modal>
        </LoadingWrapper>
    );
};

export default ZadostPosila;
